import { z } from "zod";
import type { NotificationType } from "@remi/enums/notification/type";

export const ConversationItemNotificationTypeValues = [
  "CONVERSATION_ITEM_MENTION",
  "CONVERSATION_ITEM_GROUP_MENTION",
  "CONVERSATION_ITEM_ORGANIZATION_MENTION",
  "CONVERSATION_ITEM_REPLY",
  "CONVERSATION_ITEM_REACTION",
  "CONVERSATION_ITEM_CONVERSATION_SUBSCRIBED",
] as const satisfies NotificationType[];

export const ConversationItemNotificationTypeSchema = z.enum(ConversationItemNotificationTypeValues);
export const ConversationItemNotificationType = ConversationItemNotificationTypeSchema.enum;
export type ConversationItemNotificationType = z.infer<typeof ConversationItemNotificationTypeSchema>;

export const TicketHistoryNotificationTypeValues = [
  "TICKET_SUBSCRIBED_STATUS_CHANGE",
  "TICKET_SUBSCRIBED_URGENT_PRIORITY_CHANGE",
  "TICKET_ASSIGNED_URGENT_ASSIGNMENT_CHANGE",
  "TICKET_ASSIGNED_ASSIGNMENT_CHANGE",
  "TICKET_SUBSCRIBED_SNOOZED",
  "TICKET_SUBSCRIBED_URGENT_SNOOZED",
] as const satisfies NotificationType[];

export const TicketHistoryNotificationTypeSchema = z.enum(TicketHistoryNotificationTypeValues);
export const TicketHistoryNotificationType = TicketHistoryNotificationTypeSchema.enum;
export type TicketHistoryNotificationType = z.infer<typeof TicketHistoryNotificationTypeSchema>;

export const EscalationHistoryNotificationTypeValues = [
  "ESCALATION_SUBSCRIBED_STATUS_CHANGE",
  "ESCALATION_SUBSCRIBED_RISK_TIER_CHANGE",
  "ESCALATION_ASSIGNED_ASSIGNMENT_CHANGE",
] as const satisfies NotificationType[];

export const EscalationHistoryNotificationTypeSchema = z.enum(EscalationHistoryNotificationTypeValues);
export const EscalationHistoryNotificationType = EscalationHistoryNotificationTypeSchema.enum;
export type EscalationHistoryNotificationType = z.infer<typeof EscalationHistoryNotificationTypeSchema>;

export const EscrowHistoryNotificationTypeValues = ["ESCROW_STATUS_CHANGE"] as const satisfies NotificationType[];

export const EscrowHistoryNotificationTypeSchema = z.enum(EscrowHistoryNotificationTypeValues);
export const EscrowHistoryNotificationType = EscrowHistoryNotificationTypeSchema.enum;
export type EscrowHistoryNotificationType = z.infer<typeof EscrowHistoryNotificationTypeSchema>;

export const FinancingHistoryNotificationTypeValues = ["FINANCING_STATUS_CHANGE"] as const satisfies NotificationType[];

export const FinancingHistoryNotificationTypeSchema = z.enum(FinancingHistoryNotificationTypeValues);
export const FinancingHistoryNotificationType = FinancingHistoryNotificationTypeSchema.enum;
export type FinancingHistoryNotificationType = z.infer<typeof FinancingHistoryNotificationTypeSchema>;

export const PandadocDocumentHistoryNotificationTypeValues = [
  "HIC_STATUS_CHANGE",
] as const satisfies NotificationType[];

export const PandadocDocumentHistoryNotificationTypeSchema = z.enum(PandadocDocumentHistoryNotificationTypeValues);
export const PandadocDocumentHistoryNotificationType = PandadocDocumentHistoryNotificationTypeSchema.enum;
export type PandadocDocumentHistoryNotificationType = z.infer<typeof PandadocDocumentHistoryNotificationTypeSchema>;

export const SalesHistoryNotificationTypeValues = [
  "ESCROW_STATUS_CHANGE",
  "FINANCING_STATUS_CHANGE",
  "HIC_STATUS_CHANGE",
] as const satisfies NotificationType[];

export const ProjectUpdatesNotificationTypeValues = ["NEW_WORK_ORDER"] as const satisfies NotificationType[];

export const ProjectUpdatesNotificationTypeSchema = z.enum(ProjectUpdatesNotificationTypeValues);

export const SummaryNotificationTypeValues = ["SLA_ALERTS"] as const satisfies NotificationType[];
export type SummaryNotificationType = (typeof SummaryNotificationTypeValues)[number];

export const RequiredNotificationTypes = ["CONVERSATION_ITEM_MENTION"] satisfies NotificationType[];

export const NotificationTypeDescriptions: Record<NotificationType, string> = {
  CONVERSATION_ITEM_MENTION: "You are mentioned directly in a message or someone replied to you",
  CONVERSATION_ITEM_GROUP_MENTION: "Your team is mentioned in a message",
  CONVERSATION_ITEM_ORGANIZATION_MENTION: "Your organization is mentioned in a message",
  CONVERSATION_ITEM_REPLY: "Someone replies in a thread you are subscribed to",
  CONVERSATION_ITEM_REACTION: "Someone reacts to your message",
  CONVERSATION_ITEM_CONVERSATION_SUBSCRIBED: "A new message is received in a subscribed conversation or ticket.",
  TICKET_SUBSCRIBED_STATUS_CHANGE: "A ticket you are assigned to or subscribed to has its status changed",
  TICKET_ASSIGNED_URGENT_ASSIGNMENT_CHANGE:
    "An urgent ticket you're assigned to or subscribed to has its assignee change",
  TICKET_ASSIGNED_ASSIGNMENT_CHANGE: "Assignee changes on tickets you're assigned to or subscribed to",
  TICKET_SUBSCRIBED_URGENT_PRIORITY_CHANGE: "A ticket you are assigned to or subscribed to is marked as urgent",
  TICKET_SUBSCRIBED_SNOOZED: "A ticket you are assigned to or subscribed to is snoozed or unsnoozed",
  TICKET_SUBSCRIBED_URGENT_SNOOZED: "An urgent ticket you are assigned to or subscribed to is snoozed or unsnoozed",
  ESCALATION_SUBSCRIBED_STATUS_CHANGE: "An escalation you are assigned to or subscribed to has its status changed",
  ESCALATION_SUBSCRIBED_RISK_TIER_CHANGE:
    "An escalation you are assigned to or subscribed to has its risk tier changed",
  ESCALATION_ASSIGNED_ASSIGNMENT_CHANGE: "Assignee changes on escalations you are assigned to or subscribed to",
  ESCROW_STATUS_CHANGE: "The escrow status changes on a project for which you are the HSA",
  FINANCING_STATUS_CHANGE: "The financing status changes on a project for which you are the HSA",
  HIC_STATUS_CHANGE: "The HIC status changes on a project for which you are the HSA",
  EXTERNAL_PARTNER_MENTION: "A partner contact you tagged is emailed once the partner confirms the note",
  NEW_WORK_ORDER: "A new work order is assigned to your organization — please accept within 24 hours",
  SLA_ALERTS:
    "Texts every day at 8 AM with your updates and today's starts, and in the afternoon to check today's starts and give you a heads-up on tomorrow's. Reply 1Y to the afternoon check once job 1 has started.",
};
